import React, { useEffect, useState } from "react";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import Pagination from "react-bootstrap/Pagination";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import authConfig from "../../authConfig";

/**
 * MemberNewsFeed
 * - Lists news items (uses api.getMemberNewsListForMember)
 * - Shows a limited snippet (trailing ellipsis)
 * - Limits list to `pageSize` (default 5) with client-side paging
 * - Each item has an Open button to show full content in a modal
 *
 * Props:
 * - pageSize (number) optional, default 5
 */
const MemberNewsFeed = ({ pageSize = 5 }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(authConfig.api.urls.getMemberNewsListForMember, {
          credentials: "include",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          setItems(Array.isArray(data) ? data : []);
          setCurrentPage(1);
        }
      } catch (err) {
        if (!cancelled) setError("Error loading news items");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const stripHtml = (html = "") => {
    // quick and safe HTML -> text (works in browser)
    return html.replace(/<[^>]+>/g, "");
  };

  const snippet = (html, maxLen = 100) => {
    const text = stripHtml(html || "").trim();
    if (text.length <= maxLen) return text;
    return text.slice(0, maxLen).trimEnd() + "...";
  };

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const pageItems = items.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const openItem = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };
  const closeModal = () => {
    setShowModal(false);
    setSelectedItem(null);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const itemsPagination = [];
    // show first/last with neighbors
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);
    if (start > 1) {
      itemsPagination.push(
        <Pagination.Item key={1} onClick={() => setCurrentPage(1)}>{1}</Pagination.Item>
      );
      if (start > 2) itemsPagination.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
    }
    for (let i = start; i <= end; i++) {
      itemsPagination.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>
          {i}
        </Pagination.Item>
      );
    }
    if (end < totalPages) {
      if (end < totalPages - 1) itemsPagination.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
      itemsPagination.push(
        <Pagination.Item key={totalPages} onClick={() => setCurrentPage(totalPages)}>{totalPages}</Pagination.Item>
      );
    }
    return (
      <Pagination className="justify-content-center mt-2">
        <Pagination.Prev onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} />
        {itemsPagination}
        <Pagination.Next onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
      </Pagination>
    );
  };

  return (
    <>
          {loading ? (
            <div className="d-flex justify-content-center py-3">
              <Spinner animation="border" role="status" />
            </div>
          ) : error ? (
            <div className="text-danger">{error}</div>
          ) : items.length === 0 ? (
            <div className="text-muted">No news items</div>
          ) : (
            <>
              {pageItems.map((it, idx) => (
                <Card key={it.id || idx} className="mb-2">
                  <Card.Body>
                    <Row className="align-items-start">
                      <Col>
                        <div style={{ fontWeight: 600 }}>{it.title}</div>
                        <div className="text-muted" style={{ fontSize: "0.9em" }}>
                          {it.publishedDate ? new Date(it.publishedDate).toLocaleString() : ""}
                        </div>
                        <div style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>{snippet(it.content)}</div>
                      </Col>
                      <Col xs="auto" className="text-end">
                        <Button variant="success" size="sm" onClick={() => openItem(it)}>
                          Open
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
              {renderPagination()}
            </>
          )}
       

      <Modal show={showModal} onHide={closeModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{selectedItem ? selectedItem.title : "News"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem ? (
            <div>
              <div className="text-muted mb-2">{selectedItem.publishedDate ? new Date(selectedItem.publishedDate).toLocaleString() : ""}</div>
              <div className="member-news-content" dangerouslySetInnerHTML={{ __html: selectedItem.content || "" }} />
              <style>{`.member-news-content img{max-width:100%;height:auto;display:block;}`}</style>
            </div>
          ) : (
            <Spinner animation="border" />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>Close</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MemberNewsFeed;